"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fromAppleCurrency = exports.toAppleCurrency = exports.optionalFromAppleMoney = exports.fromAppleMoney = exports.toAppleMoney = exports.optionalToAppleMoney = exports.optionalToMoneyString = exports.optionalFromMoneyString = exports.roundMoney = exports.toMoneyString = exports.fromMoneyString = void 0;
const core_1 = require("../core");
const ruby_1 = require("../ruby");
const decimal_1 = require("./decimal");
function parseNumberOrThrow(amount) {
    const value = parseFloat(amount);
    if (isNaN(value)) {
        throw new Error(`Provided value: ${amount} is not a number.`);
    }
    return value;
}
/**
 * Converts a string into a Money value.
 *
 * This will round string values with more than 2 decimal places.
 *
 * @param amount The monetary value as a string.
 * @returns A numerical representation of the provided value
 */
function fromMoneyString(amount) {
    return roundMoney(parseNumberOrThrow(amount));
}
exports.fromMoneyString = fromMoneyString;
/**
 * Converts a numerical money value to a string representation.
 *
 * Values with more than 2 decimal places will be rounded.
 *
 * @param amount The monetary value as a number
 * @returns A string representation of the provided value
 */
function toMoneyString(amount) {
    return amount.toFixed(2);
}
exports.toMoneyString = toMoneyString;
/**
 * Rounds the provided value to 2 decimal places.
 *
 * .5 and above is rounded up, under is rounded down.
 *
 * @param amount The amount to round.
 * @returns The value rounded to 2 decimal places.
 */
function roundMoney(amount) {
    return Math.round(amount * 100) / 100;
}
exports.roundMoney = roundMoney;
/**
 * Converts a string value into a numerical money value if it is defined.
 *
 * Any string value with more than 2 decimal places will be rounded.
 *
 * Returns undefined if no value is provided
 *
 * @param amount The monetary value as a string or no value
 * @returns A numerical representation of the provided value
 */
function optionalFromMoneyString(amount) {
    return amount != null ? roundMoney(parseNumberOrThrow(amount)) : undefined;
}
exports.optionalFromMoneyString = optionalFromMoneyString;
/**
 * Converts an optional numerical money value to a string.
 *
 * Returns undefined if no value is provided
 *
 * @param amount The monetary value as a number.
 * @returns A string representation of the money value.
 */
function optionalToMoneyString(amount) {
    return amount ? amount.toFixed(2) : undefined;
}
exports.optionalToMoneyString = optionalToMoneyString;
/**
 * Converts a value and currency pair into an Apple ASA compatible money value.
 *
 * Returns undefined if either of the arguments are undefined.
 *
 * @param amount The monetary value as a number.
 * @param currency The currency of the money.
 * @returns An Apple compatible representation of the money.
 */
function optionalToAppleMoney(amount, currency) {
    return amount && currency ? toAppleMoney(amount, currency) : undefined;
}
exports.optionalToAppleMoney = optionalToAppleMoney;
/**
 *
 * Converts a numerical money value and a currency into an Apple ASA compatible money value.
 *
 * @param amount The monetary value as a number.
 * @param currency The currency of the money.
 * @returns An Apple compatible representation of the money.
 */
function toAppleMoney(amount, currency) {
    return {
        amount: toMoneyString((0, decimal_1.decimalToMoney)(amount)),
        currency: toAppleCurrency(currency),
    };
}
exports.toAppleMoney = toAppleMoney;
/**
 *
 * Converts to numerical money value from Apple ASA compatible money value.
 *
 * @param amount The monetary value as a number.
 * @param currency The currency of the money.
 * @returns An Apple compatible representation of the money.
 */
function fromAppleMoney(money) {
    return fromMoneyString(money.amount);
}
exports.fromAppleMoney = fromAppleMoney;
/**
 *
 * Converts to numerical money value from Apple ASA compatible money value.
 *
 * @param amount The monetary value as a number.
 * @param currency The currency of the money.
 * @returns An Apple compatible representation of the money.
 */
function optionalFromAppleMoney(money) {
    return money != null ? fromMoneyString(money.amount) : undefined;
}
exports.optionalFromAppleMoney = optionalFromAppleMoney;
/**
 * Converts a currency value into the Apple compatible currency value.
 * @param currency The currency to convert.
 * @returns An apple compatible currency code for the given value.
 */
function toAppleCurrency(currency) {
    if (ruby_1.appleProxyCurrencies.includes(currency)) {
        return currency;
    }
    else if (core_1.supportedCurrencies.includes(currency)) {
        switch (currency) {
            case "AUD":
                return "AUD";
            case "CAD":
                return "CAD";
            case "CNY":
                return "RMB";
            case "EUR":
                return "EUR";
            case "GBP":
                return "GBP";
            case "JPY":
                return "JPY";
            case "MXN":
                return "MXN";
            case "NZD":
                return "NZD";
            case "RUB":
                return "RUB";
            case "USD":
                return "USD";
        }
    }
    throw new Error("Unsupported currency");
}
exports.toAppleCurrency = toAppleCurrency;
/**
 * Converts the provided currency from Apple into the system compatible representation.
 *
 * @param currency The Apple currency to convert.
 * @returns The system compatible currency value.
 */
function fromAppleCurrency(currency) {
    switch (currency) {
        case "AUD":
            return "AUD";
        case "CAD":
            return "CAD";
        case "EUR":
            return "EUR";
        case "GBP":
            return "GBP";
        case "JPY":
            return "JPY";
        case "MXN":
            return "MXN";
        // Chinese conversion used by Apple
        case "RMB":
            return "CNY";
        case "NZD":
            return "NZD";
        case "RUB":
            return "RUB";
        case "USD":
            return "USD";
        default:
            throw new Error("Unsupported currency");
    }
}
exports.fromAppleCurrency = fromAppleCurrency;
